import { Component, OnInit, signal, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NonNullableFormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';

import { PageSize } from 'src/app/enumeration/pageSize';
import { UserLoginRole } from 'src/app/enumeration/roles';
import { QrApiService } from 'src/app/service/qr/qr-api.service';
import { CheckRolesAccessService } from 'src/app/utils/check_roles_access/check-roles-access.service';
import { DateFormatPipe } from 'src/app/utils/date-format.pipe';
import { DownloadService } from 'src/app/utils/download/download.service';

declare var $: any;

@Component({
  selector: 'app-qr-code-list',
  standalone: true,
  imports: [
    CommonModule,
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    DateFormatPipe,
  ],
  templateUrl: './qr-code-list.component.html',
  styleUrls: ['./qr-code-list.component.css'],
})
export class QrCodeListComponent implements OnInit {
  // ─── Modern Services Injection Architecture ───
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly router = inject(Router);
  private readonly apiService = inject(QrApiService);
  private readonly downloadService = inject(DownloadService);
  private readonly checkAccessRoles = inject(CheckRolesAccessService);
  private readonly destroyRef = inject(DestroyRef);

  // ─── Component Variables Signals Engine ───
  readonly loading = signal<boolean>(false);
  readonly apiLoading = signal<boolean>(false);
  readonly apiDownloading = signal<boolean>(false);
  
  readonly page = signal<number>(0);
  readonly pageSize = signal<number>(PageSize.QR_LIST);
  readonly allTotal = signal<number>(0);
  
  readonly qrcodes = signal<any[]>([]);
  readonly isEnableBtnLoading = signal<boolean[]>([]);
  readonly selectedFile = signal<File | null>(null);

  // ─── Structural Parameters Tracking Layer ───
  fileName: string = '';
  checkSerial: string = '';
  checkPhone: string = '';
  searchSerial: string = '';
  downloadFileName: string = '';
  errorMessage: string = '';
  isApiError: boolean = false;
  
  // ─── Governance Security Access Flags ───
  isUploadNewDownloadAccess: boolean = false;
  isClaimRecordAccess: boolean = false;
  isQREnableDisableAccess: boolean = false;

  // ─── Strictly-Typed Non-Nullable Form Blueprint ───
  readonly qrCodeDataFrom: FormGroup = this.fb.group({
    serialNumber: ['', [Validators.required]],
    qrCode: ['', [Validators.required, Validators.minLength(13), Validators.pattern(/^\S*$/)]],
    points: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
  });

  ngOnInit(): void {
    this.hydrateAccessPrivileges();
    this.getQRCodeList();
  }

  /**
   * Initializes permission scopes dynamically from role control service arrays
   */
  private hydrateAccessPrivileges(): void {
    this.isUploadNewDownloadAccess = this.checkAccessRoles.check([UserLoginRole.SUPER_ADMIN]);
    this.isClaimRecordAccess = this.checkAccessRoles.check([UserLoginRole.SUPER_ADMIN, UserLoginRole.ADMIN]);
    this.isQREnableDisableAccess = this.checkAccessRoles.check([UserLoginRole.LUCKY_DRAW_ADMIN]);
  }

  /**
   * Universal filter change event receiver
   */
  textChange(value: string): void {
    this.page.set(0);
    this.getQRCodeList();
  }

  searchQRListByNumber(): void {
    this.page.set(0);
    this.getQRCodeList();
  }

  clearSearchQRListByNumber(): void {
    this.searchSerial = '';
    this.page.set(0);
    this.getQRCodeList();
  }

  /**
   * Core Paginated QR Records Ingestion Engine
   */
  getQRCodeList(): void {
    this.loading.set(true);
    this.apiService.getQRCodeList(this.searchSerial, this.page())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: (v) => {
          this.qrcodes.set(v?.qrCodeInfoLst || []);
          this.allTotal.set(v?.allTotal || 0);
        },
        error: (e) => {
          if (e) alert(e);
        }
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files?.length) {
      this.selectedFile.set(input.files[0]);
    }
  }

  /**
   * CSV Batch Data Package Ingestion Request
   */
  uploadFile(): void {
    const targetFile = this.selectedFile();
    if (!targetFile) return;

    this.apiLoading.set(true);
    this.isApiError = false;

    this.apiService.getUploadCSVQRCode(targetFile)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.apiLoading.set(false))
      )
      .subscribe({
        next: () => {
          alert('Success');
          this.selectedFile.set(null);
          $('#uploadCSVModal').modal('hide');
          this.getQRCodeList();
        },
        error: (e) => {
          this.isApiError = true;
          this.errorMessage = e || 'An error occurred during file upload';
        }
      });
  }

  /**
   * Handles Manual Direct Individual Token Add Requests
   */
  submitNewQrcode(): void {
    if (this.qrCodeDataFrom.invalid) return;

    this.apiLoading.set(true);
    this.apiService.getCreateQRCode(this.qrCodeDataFrom.getRawValue())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.apiLoading.set(false))
      )
      .subscribe({
        next: () => {
          alert('New QR code added');
          this.qrCodeDataFrom.reset();
          this.getQRCodeList();
        },
        error: (e) => {
          if (e) alert(e);
        }
      });
  }

  checkQrClaimHistory(): void {
    if (this.checkSerial) {
      this.router.navigate(['nav/qr-code-illegalclaim/', this.checkSerial]);
    }
  }

  checkClaimPhoneHistory(): void {
    if (this.checkPhone) {
      this.router.navigate(['nav/qr-code-history', this.checkPhone]);
    }
  }

  qrCodeDetail(serialNumber: number): void {
    this.router.navigate(['nav/qr-code-details', serialNumber]);
  }

  /**
   * Toggles active state verification checks dynamically per individual row reference index
   */
  enableQrcode(serialNumber: number, type: string, index: number): void {
    if (!confirm(type)) return;

    // Mutate state tracking array layer safely using signal values updates mechanics
    this.isEnableBtnLoading.update((states) => {
      states[index] = true;
      return [...states];
    });

    this.apiService.getEnableQrcode(type, String(serialNumber))
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isEnableBtnLoading.update((states) => {
            states[index] = false;
            return [...states];
          });
        })
      )
      .subscribe({
        next: () => {
          alert('Success');
          this.getQRCodeList();
        },
        error: (e) => {
          if (e) alert(e);
        }
      });
  }

  handlePageChange(event: number): void {
    if (!this.loading()) {
      this.page.set(event - 1);
      this.getQRCodeList();
    }
  }

  /**
   * Data Ledger Extract Synchronization Processing Task
   */
  downloadFile(): void {
    if (!this.downloadFileName.trim()) {
      alert('Please enter a target file name');
      return;
    }

    this.apiDownloading.set(true);
    
    if (!this.downloadFileName.endsWith('.csv')) {
      this.downloadFileName += '.csv';
    }

    this.apiService.getDownloadFile(this.downloadFileName)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.downloadFileName = '';
          this.apiDownloading.set(false);
          $('#downloadCSVModal').modal('hide');
        })
      )
      .subscribe({
        next: (value) => {
          this.downloadService.downloadFileInLocal(value, this.downloadFileName);
        },
        error: (e) => {
          if (e) alert(e);
        }
      });
  }
}
